import React, { useEffect, useState } from "react";
import { Activity, Cpu, Zap, Clock, Server } from "lucide-react";

/**
 * Fake live telemetry bar — gives the "production system" vibe.
 * Numbers drift slightly every few seconds.
 */
const useTicker = (base, variance, interval = 2400) => {
  const [val, setVal] = useState(base);
  useEffect(() => {
    const id = setInterval(() => {
      const drift = (Math.random() - 0.5) * variance * 2;
      setVal(Math.max(0, base + drift));
    }, interval);
    return () => clearInterval(id);
  }, [base, variance, interval]);
  return val;
};

const Pill = ({ icon: Icon, label, value, accent = "cyan" }) => {
  const accentText = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300"
  }[accent];
  return (
    <div className="flex items-center gap-2 shrink-0">
      <Icon className={`w-3 h-3 ${accentText}`} />
      <span className="font-mono text-[10px] tracking-wider uppercase text-zinc-500">
        {label}
      </span>
      <span className={`font-mono text-[11px] ${accentText}`}>{value}</span>
    </div>
  );
};

const MetricsBar = () => {
  const heap = useTicker(34, 4);
  const rps = useTicker(142, 24, 1800);
  const p99 = useTicker(28, 5, 2000);
  const lag = useTicker(0, 2, 3000);
  const uptime = 99.98;

  return (
    <div className="fixed top-16 md:top-20 inset-x-0 z-40 border-b border-white/5 bg-[#050507]/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-9 flex items-center gap-5 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-300">
            live
          </span>
        </div>
        <span className="text-zinc-700 shrink-0">|</span>
        <Pill icon={Cpu} label="jvm heap" value={`${heap.toFixed(0)}%`} />
        <Pill icon={Zap} label="rps" value={rps.toFixed(0)} accent="amber" />
        <Pill icon={Clock} label="p99" value={`${p99.toFixed(0)}ms`} />
        <Pill icon={Server} label="uptime" value={`${uptime}%`} accent="emerald" />
        <Pill icon={Activity} label="kafka lag" value={lag.toFixed(0)} accent="amber" />
        <span className="ml-auto font-mono text-[10px] text-zinc-600 shrink-0 hidden md:inline">
          press <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300">⌘ K</kbd>
        </span>
      </div>
    </div>
  );
};

export default MetricsBar;
